import React, { useContext, useEffect, useRef, useState } from 'react'

import { ScrollArea } from '@rewardops/ui'

import { ResizeContext } from '~/context/resize-context'

interface DynamicScrollAreaProps {
  children: React.ReactNode
  maxHeightClass?: string // Maximum height for the scroll area
  className?: string
}

const DynamicScrollArea = ({
  children,
  maxHeightClass = 'h-[25vh]',
  className = '',
  ...props
}: DynamicScrollAreaProps) => {
  const [isScrollable, setIsScrollable] = useState(false)
  const [maxHeight, setMaxHeight] = useState<number>(0)
  const scrollAreaRef = useRef<HTMLDivElement | null>(null)
  const windowSize = useContext(ResizeContext)

  // Helper to calculate the max height dynamically
  const calculateMaxHeight = () => {
    const heightMapping: Record<string, number> = {
      'h-[20vh]': window.innerHeight * 0.2,
      'h-[25vh]': window.innerHeight * 0.25,
      'h-[50vh]': window.innerHeight * 0.5,
      'h-[75vh]': window.innerHeight * 0.75,
      'h-[100vh]': window.innerHeight,
    }
    return heightMapping[maxHeightClass] || 0
  }

  // Recalculate scrollability and max height on screen changes
  const updateScrollability = () => {
    if (scrollAreaRef.current) {
      const { scrollHeight } = scrollAreaRef.current
      const newMaxHeight = calculateMaxHeight()
      setMaxHeight(newMaxHeight)
      setIsScrollable(scrollHeight > newMaxHeight)
    }
  }

  useEffect(() => {
    // Initial calculation
    updateScrollability()
  }, [children, maxHeightClass, windowSize])

  return (
    <ScrollArea
      ref={scrollAreaRef}
      className={`${className} ${isScrollable ? maxHeightClass : 'h-auto'}`}
      style={{ maxHeight: maxHeight ? `${maxHeight}px` : undefined }}
      forceScrollBarMount
      {...props}
    >
      {children}
    </ScrollArea>
  )
}

export default DynamicScrollArea
